/**
 * A game's public results, for the share panel: their status (made, published,
 * the link, the Discord posts), and the admin's actions on them. Addressed by
 * the game's database id, so archived games work too. Every call is answered
 * on its ack.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useSocketContext } from '@swtorbit/shared-react-hooks';
import {
  SocketEvent,
  type Game,
  type ResultsListDiscordTargetsResponse,
  type ResultsPostDestination,
  type ResultsPostDiscordResponse,
  type ResultsStatus,
} from '@swtorbit/shared-socket-events';

const TIMEOUT_MS = 60_000;

export interface GameResultsActions {
  status: ResultsStatus | null;
  loading: boolean;
  /** The last action's error, until the next one. */
  error: string | null;
  refresh: () => void;
  publish: () => Promise<void>;
  unpublish: () => Promise<void>;
  rebuild: () => Promise<void>;
  setAutoPublish: (autoPublish: boolean) => Promise<void>;
  listTargets: (guildId: string) => Promise<ResultsListDiscordTargetsResponse>;
  post: (guildId: string, destination: ResultsPostDestination) => Promise<ResultsPostDiscordResponse>;
}

export function useGameResults(game: Pick<Game, 'dbId' | 'status'> | null): GameResultsActions {
  const { socket } = useSocketContext();
  const [status, setStatus] = useState<ResultsStatus | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const gameId = game?.dbId ?? null;
  const gameIdRef = useRef(gameId);
  gameIdRef.current = gameId;

  /** Emit for this game and resolve with the ack (or a timeout's error). */
  const request = useCallback(
    <T>(event: SocketEvent, payload: object, fail: (error: string) => T): Promise<T> => {
      if (!socket || gameIdRef.current === null) return Promise.resolve(fail('Not connected'));
      return new Promise((resolve) => {
        socket.timeout(TIMEOUT_MS).emit(event, { gameId: gameIdRef.current, ...payload }, (err: Error | null, response?: T) => {
          resolve(err || !response ? fail('The server did not answer') : response);
        });
      });
    },
    [socket],
  );

  const statusCall = useCallback(
    async (event: SocketEvent, payload: object = {}) => {
      const forGame = gameIdRef.current;
      setLoading(true);
      setError(null);
      const response = await request<ResultsStatus>(event, payload, (message) => ({ success: false, error: message }) as ResultsStatus);
      if (gameIdRef.current !== forGame) return;
      setLoading(false);
      if (response.success) setStatus(response);
      else setError(response.error ?? 'Something went wrong');
    },
    [request],
  );

  const refresh = useCallback(() => void statusCall(SocketEvent.RESULTS_ADMIN_GET_STATUS), [statusCall]);

  // Again when the game changes or ends (its results are made then).
  useEffect(() => {
    setStatus(null);
    if (gameId !== null) refresh();
  }, [gameId, game?.status, refresh]);

  return {
    status,
    loading,
    error,
    refresh,
    publish: () => statusCall(SocketEvent.RESULTS_ADMIN_PUBLISH),
    unpublish: () => statusCall(SocketEvent.RESULTS_ADMIN_UNPUBLISH),
    rebuild: () => statusCall(SocketEvent.RESULTS_ADMIN_REBUILD),
    setAutoPublish: (autoPublish: boolean) => statusCall(SocketEvent.RESULTS_ADMIN_SET_AUTO_PUBLISH, { autoPublish }),
    listTargets: (guildId: string) =>
      request<ResultsListDiscordTargetsResponse>(SocketEvent.RESULTS_ADMIN_LIST_DISCORD_TARGETS, { guildId }, (message) => ({
        success: false,
        error: message,
      })),
    post: async (guildId: string, destination: ResultsPostDestination) => {
      const response = await request<ResultsPostDiscordResponse>(SocketEvent.RESULTS_ADMIN_POST_DISCORD, { guildId, destination }, (message) => ({
        success: false,
        error: message,
      }));
      // The posts list changed.
      if (response.success) refresh();
      return response;
    },
  };
}
